import axios from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import { RecurrenceFrequency, RecurringRevenueStatus } from '../models/recurringRevenue';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

export interface ConvertToRecurringInput {
  invoiceId: string;
  frequency: RecurrenceFrequency;
  customIntervalDays?: number | null;
  // item 298 — card opt-in for this subscription's invoices (ACH always offered)
  allowCard?: boolean;
  serviceCategory?: string;
  // item 169 — optional per-rep monthly commission % (null = default from the rep's rate)
  outsideRepPercent?: number | null;
  insideRepPercent?: number | null;
  // item 295 — optional rep overrides (null = the customer's assigned Outside/Inside rep)
  outsideRepUserId?: string | null;
  insideRepUserId?: string | null;
}

// item 731 (Chad) — an editable subscription line for the go-forward money edit
export interface EditRecurringLineInput {
  sku?: string;
  brand?: string;
  description: string;
  monthlyAmount: number;
  itemId?: string | null;
}

// item 170 — edit a schedule go-forward
export interface EditRecurringInput {
  id: string;
  name: string;
  amount: number;
  frequency: RecurrenceFrequency;
  customIntervalDays?: number | null;
  // item 298 — card opt-in for this subscription's invoices (ACH always offered)
  allowCard?: boolean;
  serviceCategory?: string;
  // item 169 — edit the per-rep monthly commission % (null = keep current)
  outsideRepPercent?: number | null;
  insideRepPercent?: number | null;
  // item 746 (Chad) — client PO # arriving after enrollment: re-pushes the invoice label so
  // future monthly invoices carry it. undefined = leave unchanged; '' = clear.
  customerPoNumber?: string;
  // item 1424 — invoice header/footer notes; undefined = leave unchanged, '' = clear
  invoiceHeaderNote?: string;
  invoiceFooterNote?: string;
  // item 731 (Chad) — when present, REPLACES the line breakdown going forward; the new total
  // drives both Basix and (for a live Stripe subscription) a going-forward-only price update.
  lines?: EditRecurringLineInput[];
}

// item 63 — convert an invoice into a recurring schedule + pause/resume/cancel it.
// item 170 — edit + archive/restore (never delete).
const useRecurringRevenueActions = () => {
  const queryClient = useQueryClient();
  // dart (2026-07-15, Chad) — NEXT_EPIC #3: also refresh the sidebar's project LIST ('projects', which
  // carries HasSubscription) + the project detail + dashboard, so the "recurring" indicator clears the
  // moment a subscription is Cancelled (the backend already excludes Cancelled from HasSubscription).
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries('recurringRevenue'),
      queryClient.invalidateQueries('stripe-next-invoice'), // item 1422 — a saved PO re-pushes the label
      queryClient.invalidateQueries('projects'),
      queryClient.invalidateQueries('project'),
      queryClient.invalidateQueries('invoices'),
      // item 741 (Chad) — cancel reverses the current unpaid month's accruals + drops the sub's
      // UPCOMING rows: refresh the commission ledger so both disappear live.
      queryClient.invalidateQueries('commissions'),
    ]);
  };

  const convertMutation = useMutation(
    (input: ConvertToRecurringInput) => axios
      .post(`${apiUrl()}RecurringRevenue/Convert`, input)
      .then((r) => r.data as string),
    { onSettled: invalidate },
  );

  const statusMutation = useMutation(
    (vars: { id: string; status: RecurringRevenueStatus }) => axios.post(
      `${apiUrl()}RecurringRevenue/SetStatus/${vars.id}`,
      vars.status,
      { headers: { 'content-type': 'application/json' } },
    ),
    { onSettled: invalidate },
  );

  const updateMutation = useMutation(
    (input: EditRecurringInput) => axios.post(`${apiUrl()}RecurringRevenue/${input.id}`, input),
    { onSettled: invalidate },
  );

  const archiveMutation = useMutation(
    (vars: { id: string; archive: boolean }) => axios.post(
      `${apiUrl()}RecurringRevenue/Archive/${vars.id}`,
      vars.archive,
      { headers: { 'content-type': 'application/json' } },
    ),
    { onSettled: invalidate },
  );

  // item 729 — reconcile with Stripe on demand (deleted/canceled at Stripe → cancelled here)
  const syncMutation = useMutation(
    (id: string) => axios.post(`${apiUrl()}payment/sync/${id}`).then((r) => r.data),
    { onSettled: invalidate },
  );

  // item 731 (Chad) — re-email the subscription's latest Stripe invoice to the client
  const resendInvoiceMutation = useMutation(
    (id: string) => axios.post(`${apiUrl()}payment/resend-invoice/${id}`).then((r) => r.data),
  );

  // item 731 (Chad) — reconcile ALL subs with Stripe in one click (deleted at Stripe → Cancelled)
  const syncAllMutation = useMutation(
    () => axios.post(`${apiUrl()}payment/sync-all`).then((r) => r.data as { reconciled: number; cancelled: number }),
    { onSettled: invalidate },
  );

  return {
    convert: (input: ConvertToRecurringInput) => convertMutation.mutateAsync(input),
    isConverting: convertMutation.isLoading,
    setStatus: (id: string, status: RecurringRevenueStatus) => statusMutation.mutate({ id, status }),
    update: (input: EditRecurringInput) => updateMutation.mutateAsync(input),
    isUpdating: updateMutation.isLoading,
    setArchive: (id: string, archive: boolean) => archiveMutation.mutate({ id, archive }),
    syncFromStripe: (id: string) => syncMutation.mutateAsync(id),
    resendInvoice: (id: string) => resendInvoiceMutation.mutateAsync(id),
    syncAllFromStripe: () => syncAllMutation.mutateAsync(),
    isSyncingAll: syncAllMutation.isLoading,
  };
};

export default useRecurringRevenueActions;
